import React, { useState, useEffect } from "react";
import ChatMessageKit from "../../../data/ChatMessageKit";

export function usePrivateChat({ me, urlParams, maxOpenInboxes = 3 }) {
  //local state
  const [forceWindowOpen, setForceWindowOpen] = useState("");
  const [unreadMessages, setUnreadMessages] = useState({});
  const [userInboxList, setUserInboxList] = useState(null);
  const [chatWindowList, setChatWindowList] = useState(null);
  const [chatIndicatorList, setChatIndicatorList] = useState(null);
  const [hasBoundFirebaseListeners, setHasBoundFirebaseListeners] =
    useState(false);
  
  const messageKit = ChatMessageKit.getUserInbox({ eventUserId: me?.id });

  useEffect(() => {
    messageKit?.getInitial((snapshot) => {
      setUserInboxList(snapshot.val() || {});
    });

    return () => {
      messageKit?.off();
    };
  }, []);

  useEffect(() => {
    if (userInboxList && !hasBoundFirebaseListeners) {
      setHasBoundFirebaseListeners(true);
      messageKit?.onChildAdded((snapshot) => {
        if (isNewlyAddedChatByUser(snapshot.key, snapshot.val()))
          setForceWindowOpen(snapshot.key);

        setUserInboxList((prevState) => {
          return { ...prevState, [snapshot.key]: snapshot.val() };
        });
      });

      messageKit?.onChildChanged((snapshot) => {
        // console.log('child changed', snapshot.val())
        setUserInboxList((prevState) => {
          return { ...prevState, [snapshot.key]: snapshot.val() };
        });
      });
    }
  }, [userInboxList]);

  useEffect(() => {
    if (userInboxList && urlParams) {
      setChatWindowList(getChatWindowList());
      setChatIndicatorList(getChatIndicatorList());
    }
  }, [userInboxList, urlParams]);

  const isNewlyAddedChatByUser = (inboxUUID, userInbox) => {
    return (
      userInboxList !== null &&
      typeof userInboxList[inboxUUID] === "undefined" &&
      userInbox &&
      userInbox.inviter
    );
  };

  const onSetUnreadMessageCount = ({
    inboxUUID,
    listItem,
    unreadMessageCount,
  }) => {
    setUnreadMessages((prevState) => {
      return {
        ...prevState,
        [inboxUUID]: {
          count: unreadMessageCount,
          isListItem: Boolean(listItem),
        },
      };
    });
  };

  const setUserInboxLastActivity = (inboxUUID, isArchived) => {
    ChatMessageKit.setUserInboxLastActivity({
      inboxUUID,
      isArchived,
      ...me,
    });
  };

  const getSortedInboxList = () => {
    const sortedByTimestamp = Object.entries(userInboxList).sort(
      (inbox1, inbox2) => {
        return inbox2[1].lastActivity - inbox1[1].lastActivity;
      }
    );
    return sortedByTimestamp;
  };

  const getChatWindowList = () => {
    const chatWindowList = getSortedInboxList()
      .filter((inbox, i) => {
        return !inbox[1].isArchived && i < maxOpenInboxes;
      })
      .map((inbox) => {
        const [inboxUUID, data] = inbox;

        const memberList = Object.entries(data.members)
          .filter(([eventUserId, data]) => eventUserId != me.id)
          .map(([eventUserId, data]) => data);

        const props = {
          // props that will be passed to ChatWindow instance
          me,
          type: "private",
          memberList,
          key: inboxUUID,
          onSetUnreadMessageCount,
          urlParams: {
            ...urlParams,
            inboxUUID,
          },
          forceOpen: () => inboxUUID === forceWindowOpen,
          oldestLastActiveTimestamp: getOldestLastActiveTimestamp(),
        };

        return [inboxUUID, { ...data, props }];
      });

    return Object.fromEntries(chatWindowList);
  };

  const getChatIndicatorList = () => {
    const chatIndicatorList = {};
    const inboxList = getSortedInboxList().filter((inbox, i) => {
      return inbox[1].isArchived || i >= maxOpenInboxes;
    });

    inboxList.forEach((inbox) => {
      const [inboxUUID, data] = inbox;

      ChatMessageKit.setUserInboxArchived({
        inboxUUID,
        isArchived: true,
        ...me,
      });

      const memberList = data.members;
      const listItemTitle = memberList
        ? getTitleFromMemberList(memberList)
        : "";

      const props = {
        // props that will be passed to ChatWindow instance
        memberList,
        listItemTitle,
        type: "private",
        me,
        listItem: true,
        onListItemClick: () => handleListItemClick(inboxUUID),
        onSetUnreadMessageCount,
        key: inboxUUID + "-indicator",
        unreadMessagesFilterFunction: (message) =>
          message.author.eventUserId != me.id,
        urlParams: {
          ...urlParams,
          inboxUUID,
        },
      };

      chatIndicatorList[inboxUUID] = {
        ...data,
        props,
      };
    });

    return chatIndicatorList;
  };

  const getTitleFromMemberList = (memberList) => {
    memberList = Object.entries(memberList)
      .filter((member) => member[0] != me.id)
      .map((member) => member[1]);

    const firstMember = memberList[0]
      ? `${memberList[0].first_name} ${memberList[0].last_name}`
      : "";
    const remainingMemberCount = memberList
      ? `${memberList.slice(1).length}`
      : "";
    const title = `${firstMember} ${
      remainingMemberCount > 0 ? "and " + remainingMemberCount + " more" : ""
    }`;

    return title;
  };

  const getOldestLastActiveTimestamp = () => {
    const timeStamp = getSortedInboxList().reverse()[0][1].lastActivity;
    return typeof timeStamp === "number" ? timeStamp : 0;
  };

  const handleListItemClick = (inboxUUID) => {
    setUserInboxLastActivity(inboxUUID, false);
    setForceWindowOpen(inboxUUID);
  };

  return {
    privateChatList: chatWindowList,
    privateChatIndicatorList: chatIndicatorList,
    unreadMessages,
  };
}
